import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom"; 
import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import { useSupportChat } from "../context/SupportChatContext";
import { useTheme } from "../context/ThemeContext";
import UserTopbar from "../components/UserTopbar"; 
import Swal from 'sweetalert2';

export default function Profile() {
  const { user, logout, updateUserData, socket } = useAuth();
  const { unreadCount, setUnreadCount } = useSupportChat();
  const { colors, isDarkMode, toggleTheme } = useTheme(); 
  const location = useLocation();

  const [activeModal, setActiveModal] = useState(null); 
  const [settingsView, setSettingsView] = useState("menu"); 
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewFileName, setPreviewFileName] = useState("");
  const [isEmailVisible, setIsEmailVisible] = useState(false); 
  
  const [formData, setFormData] = useState({
    name: "",
    currentPassword: "",
    newPassword: "",
    currentPin: "",
    newPin: ""
  });

  const chatEndRef = useRef();
  const fileRef = useRef();
  const profileFileRef = useRef(); 

  const processedIds = useRef(new Set());

  const userId = user?._id;
  const currentLevel = user?.memberLevel || 1;

  const handleLogout = () => {
    Swal.fire({
      title: 'Terminate Session?',
      text: "You will need to re-authenticate to access your account.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: colors.brand,
      cancelButtonColor: colors.muted,
      confirmButtonText: 'Yes, Disconnect',
      cancelButtonText: 'Cancel',
      background: colors.card,
      color: colors.text
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
      }
    });
  };

  useEffect(() => {
    if (location.state?.openSupport || location.state?.openChat) {
      setActiveModal('support');
      setInputText("Hey there, i want to process the payment manually, please assist me.");
    }
  }, [location.state]);

  const getTier = (level) => {
    if (level >= 10) return { name: "Class A (Priority)", color: "#F59E0B" };
    if (level >= 5) return { name: "Class B (Standard)", color: "#94A3B8" };
    return { name: "Class C (Restricted)", color: "#B45309" };
  };

  const tier = getTier(currentLevel);
  const progressPercent = Math.min((currentLevel / 10) * 100, 100);

  const getFullImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const API_BASE = "https://golden-shaina-maritimesupplysuites-ed826a4c.koyeb.app/api";
    const cleanPath = path.replace(/\\/g, "/").replace(/^(\/|\\)?uploads(\/|\\)/i, "").replace(/^\/+/, "");
    return `${API_BASE}/uploads/${cleanPath}`;
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
        return Swal.fire({ icon: 'error', title: 'File Too Large', text: 'Max limit is 5MB', background: colors.card, color: colors.text });
    }
    const data = new FormData();
    data.append("image", file);
    data.append("userId", userId);
    try {
      setFormLoading(true);
      const res = await api.post("/api/user/upload-profile", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data.user) {
        updateUserData(res.data.user);
        Swal.fire({ 
            icon: 'success', title: 'Biometrics Updated', text: 'Identity image synchronized.', 
            background: colors.card, color: colors.text, timer: 2000, showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({ 
          icon: 'error', title: 'Upload Failed', text: err.response?.data?.message || 'Secure server rejected the file.', 
          background: colors.card, color: colors.text 
      });
    } finally {
      setFormLoading(false);
      if (profileFileRef.current) profileFileRef.current.value = "";
    }
  };

  const handleUpdate = async (type) => {
    setFormLoading(true);
    let endpoint = "";
    let payload = {};
    if (type === "name") {
      endpoint = "/api/user/update-name";
      payload = { name: formData.name };
    } else if (type === "password") {
      endpoint = "/api/user/update-password";
      payload = { currentPassword: formData.currentPassword, newPassword: formData.newPassword };
    } else if (type === "pin") {
      endpoint = "/api/user/update-pin";
      payload = { currentPin: formData.currentPin, newPin: formData.newPin };
    }
    try {
      const { data } = await api.put(endpoint, payload);
      if (data.user) updateUserData(data.user);
      Swal.fire({
        icon: 'success', title: 'Registry Updated', text: 'Node credentials synchronized successfully.',
        confirmButtonColor: colors.brand, background: colors.card, color: colors.text
      });
      setSettingsView("menu");
      setFormData({ name: "", currentPassword: "", newPassword: "", currentPin: "", newPin: "" });
    } catch (err) {
      Swal.fire({
        icon: 'error', title: 'Verification Failed', text: err.response?.data?.message || "Internal network error",
        background: colors.card, color: colors.text
      });
    } finally {
      setFormLoading(false);
    }
  };

  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    setIsRefreshing(true);
    try {
      const { data } = await api.get(`/api/chat/history`);
      data.forEach(m => processedIds.current.add(m._id));
      const uniqueMessages = Array.from(new Map(data.map(m => [m._id, m])).values());
      setMessages(uniqueMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
      if (activeModal === 'support') {
        setUnreadCount(0);
        await api.put(`/api/chat/seen/${userId}`);
      }
    } catch (err) { 
      console.error("Comms sync error", err); 
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  }, [userId, activeModal, setUnreadCount]);

  useEffect(() => {
    if (activeModal === 'support') {
      fetchHistory();
      if (socket) {
        socket.emit("join", { userId, role: user?.role });
        socket.emit("checkAdminStatus");
      }
    }
  }, [activeModal, fetchHistory, socket, userId, user?.role]);

  useEffect(() => {
    if (activeModal === 'support') {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeModal]);

  useEffect(() => {
    if (!socket || !userId) return;
    
    const handleNewMessage = (msg) => {
      if (processedIds.current.has(msg._id) || msg.senderRole === "user") return;

      processedIds.current.add(msg._id);
      setMessages((prev) => {
        if (prev.some(m => m._id === msg._id)) return prev;
        return [...prev, msg].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      });

      if (activeModal === 'support') {
        api.put(`/api/chat/seen/${userId}`).catch(() => {});
      } else if (msg.senderRole === "admin") {
        setUnreadCount(prev => prev + 1);
      }
    };

    const handleAdminStatus = (status) => setAdminOnline(status);
    const handleUnreadUpdate = (data) => {
      if (data.userId === userId && activeModal !== 'support') {
        setUnreadCount(data.count);
      }
    };
    
    socket.on("newMessage", handleNewMessage);
    socket.on("admin:status", handleAdminStatus);
    socket.on("unreadUpdate", handleUnreadUpdate);
    
    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("admin:status", handleAdminStatus);
      socket.off("unreadUpdate", handleUnreadUpdate);
    };
  }, [socket, userId, activeModal, setUnreadCount]);

  const sendSupportMessage = async () => {
    if (formLoading || (!inputText.trim() && !fileRef.current?.files?.[0])) return;
    
    const tempId = `temp-${Date.now()}`;
    const originalText = inputText;
    const fileToUpload = fileRef.current?.files?.[0];

    const optimisticMsg = {
      _id: tempId,
      text: originalText,
      senderRole: "user",
      userId: userId,
      createdAt: new Date().toISOString(),
      isOptimistic: true
    };

    processedIds.current.add(tempId);
    setMessages(prev => [...prev, optimisticMsg]);
    setInputText("");
    setPreviewFileName("");

    const form = new FormData();
    form.append("userId", userId);
    form.append("text", originalText);
    form.append("senderRole", "user");
    if (fileToUpload) form.append("file", fileToUpload);

    try {
      setFormLoading(true);
      const { data } = await api.post("/api/chat/send", form, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      processedIds.current.add(data._id);
      setMessages(prev => prev.map(m => m._id === tempId ? data : m));
      
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { 
      console.error("Uplink failed");
      setMessages(prev => prev.filter(m => m._id !== tempId));
      setInputText(originalText);
      Swal.fire({ icon: 'error', title: 'Transmission Blocked', text: '📧 support@maritimesupplysuite.shop', background: colors.card, color: colors.text });
    } finally {
      setFormLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div style={{...styles.layout, backgroundColor: colors.bg}}>
      <UserTopbar title="" />

      <div style={styles.container}>
        <div style={{...styles.card, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
          <div style={styles.profileHeader}>
            <div style={styles.avatarWrapper}>
              <img 
                src={user.profileImage ? getFullImageUrl(user.profileImage) : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png"} 
                style={{...styles.avatarImg, border: `3px solid ${colors.brand}`}} 
                alt="Profile" 
                onClick={() => profileFileRef.current.click()} 
              />
              <label style={{...styles.uploadIcon, background: colors.brand, border: `2px solid ${colors.card}`}} onClick={() => profileFileRef.current.click()}>
                <input type="file" ref={profileFileRef} hidden onChange={handleProfileImageUpload} accept="image/*" />
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                  <circle cx="12" cy="13" r="4"></circle>
                </svg>
              </label>
            </div>
            <div style={styles.tierRow}>
              <div style={{...styles.tierBadge, border: `1px solid ${tier.color}`, color: tier.color}}>{tier.name}</div>
              <div style={styles.levelBadge}>Rank: {currentLevel}</div>
            </div>
          </div>

          <div style={styles.progressBox}>
            <div style={styles.progressLabels}>
              <span>Operational Clearance Progress</span>
              <span>{progressPercent}%</span>
            </div>
            <div style={{...styles.progressBar, backgroundColor: isDarkMode ? "#0f172a" : "#e2e8f0"}}>
              <div style={{...styles.progressFill, backgroundColor: colors.brand, width: `${progressPercent}%`}} />
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <div style={styles.userEmail}>My Email: {isEmailVisible ? user?.email : '••••••••••••'}</div>
            <button onClick={() => setIsEmailVisible(!isEmailVisible)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: 0 }}>
              {isEmailVisible ? '🔓' : '🔒'}
            </button>
          </div>
        </div>

        <div style={{...styles.menuGroup, backgroundColor: colors.card, border: `1px solid ${colors.border}`}}>
          <button style={{...styles.menuItem, borderBottom: `1px solid ${colors.border}`}} onClick={toggleTheme}>
            <span style={{...styles.menuText, color: colors.text}}>🌗 Theme</span>
            <span style={{...styles.menuTextMuted, color: colors.brand, fontWeight: '800'}}>{isDarkMode ? "☀️" : "🌙"}</span>
          </button>
          <button onClick={() => window.location.href='/manual'} style={{...styles.menuItem, borderBottom: `1px solid ${colors.border}`, textAlign: 'left'}}>
            <span style={{...styles.menuText, color: colors.text}}>📜 User Guide</span>
            <span style={{...styles.menuTextMuted, color: colors.brand, fontWeight: '700'}}>📖</span>
          </button>
          <button style={{...styles.menuItem, borderBottom: `1px solid ${colors.border}`}} onClick={() => { setActiveModal('settings'); setSettingsView('menu'); }}>
            <span style={{...styles.menuText, color: colors.text}}>⚙️ Settings</span>
            <span style={styles.menuArrow}>&rsaquo;</span>
          </button>
          <button style={{...styles.menuItem, borderBottom: `1px solid ${colors.border}`}} onClick={() => setActiveModal('language')}>
            <span style={{...styles.menuText, color: colors.text}}>🌐 Language</span>
            <span style={styles.menuArrow}>&rsaquo;</span>
          </button>
          <button style={{...styles.menuItem, borderBottom: `1px solid ${colors.border}`}} onClick={() => setActiveModal('support')}>
            <div style={{display:'flex', alignItems:'center'}}>
              <span style={{...styles.menuText, color: colors.text}}>📢 Customer Support</span>
              {unreadCount > 0 && <span style={styles.badge}>{unreadCount}</span>}
            </div>
            <span style={styles.menuArrow}>&rsaquo;</span>
          </button>
          <button style={{...styles.menuItem, border: 'none'}} onClick={handleLogout}>
            <span style={{...styles.menuText, color: colors.danger}}>🚪 Terminate Session</span>
          </button>
        </div>
      </div>

      {/* MODALS */}
      {activeModal === 'language' && (
        <div style={styles.overlay} onClick={() => setActiveModal(null)}>
          <div style={{...styles.modal, backgroundColor: colors.card, border: `1px solid ${colors.border}`}} onClick={e => e.stopPropagation()}>
            <h3 style={{...styles.modalTitle, color: colors.text}}>Interface Language</h3>
            <div style={styles.verticalStack}>
                {["English", "Mandarin", "Spanish", "Arabic", "French", "Italian", "Hindi"].map(lang => (
                    <button key={lang} style={{...styles.stackBtn, color: colors.text, border: `1px solid ${colors.border}`, backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.5)" : "#f9f1f1"}} onClick={() => lang !== "English" && Swal.fire({title: 'Error 404 (Content missing)', background: colors.card, color: colors.text})}>
                        {lang} {lang === "English" && "(Active)"}
                    </button>
                ))}
            </div>
            <button style={styles.closeLink} onClick={() => setActiveModal(null)}>Close</button>
          </div>
        </div>
      )}

      {activeModal === 'settings' && (
        <div style={styles.overlay} onClick={() => setActiveModal(null)}>
          <div style={{...styles.modal, backgroundColor: colors.card, border: `1px solid ${colors.border}`}} onClick={e => e.stopPropagation()}>
            <h3 style={{...styles.modalTitle, color: colors.text}}>Protocol Encryption</h3>
            {settingsView === "menu" ? (
              <div style={styles.verticalStack}>
                <button style={{...styles.stackBtn, color: colors.text, border: `1px solid ${colors.border}`, backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.5)" : "#f1f5f9"}} onClick={() => setSettingsView("name")}>Identity Tag</button>
                <button style={{...styles.stackBtn, color: colors.text, border: `1px solid ${colors.border}`, backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.5)" : "#f1f5f9"}} onClick={() => setSettingsView("password")}>Access Password</button>
                <button style={{...styles.stackBtn, color: colors.text, border: `1px solid ${colors.border}`, backgroundColor: isDarkMode ? "rgba(15, 23, 42, 0.5)" : "#f1f5f9"}} onClick={() => setSettingsView("pin")}>Transaction PIN</button>
                <button style={styles.closeLink} onClick={() => setActiveModal(null)}>Cancel</button>
              </div>
            ) : (
              <div style={styles.verticalStack}>
                {settingsView === "name" && (
                  <input style={{...styles.input, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} placeholder="New Identity Tag" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                )}
                {settingsView === "password" && (
                  <>
                    <input style={{...styles.input, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} type="password" placeholder="Current Password" value={formData.currentPassword} onChange={(e) => setFormData({...formData, currentPassword: e.target.value})} />
                    <input style={{...styles.input, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} type="password" placeholder="New Password" value={formData.newPassword} onChange={(e) => setFormData({...formData, newPassword: e.target.value})} />
                  </>
                )}
                {settingsView === "pin" && (
                  <>
                    <input style={{...styles.input, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} type="password" placeholder="Current PIN" value={formData.currentPin} onChange={(e) => setFormData({...formData, currentPin: e.target.value})} />
                    <input style={{...styles.input, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} type="password" placeholder="New PIN" value={formData.newPin} onChange={(e) => setFormData({...formData, newPin: e.target.value})} />
                  </>
                )}
                <button style={{...styles.actionBtn, backgroundColor: colors.brand}} onClick={() => handleUpdate(settingsView)} disabled={formLoading}>
                  {formLoading ? "Synchronizing..." : "Update Protocol"}
                </button>
                <button style={styles.closeLink} onClick={() => setSettingsView("menu")}>Return</button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeModal === 'support' && (
        <div style={styles.overlay} onClick={() => setActiveModal(null)}>
          <div style={{...styles.chatWindow, backgroundColor: colors.card, border: `1px solid ${colors.border}`}} onClick={e => e.stopPropagation()}>
            <div style={{...styles.chatHeader, backgroundColor: isDarkMode ? "#0a1120" : colors.brand}}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={styles.chatTitle}>Customer Support</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: adminOnline ? '#00ff22' : '#ff0202' }} />
                  <div style={{...styles.chatStatus, color: isDarkMode ? colors.brand : "#fff"}}>{adminOnline ? "LIVE NOW..." : "OFFLINE, LEAVE A MESSAGE..."}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={fetchHistory} style={styles.refreshBtn}>
                  <span style={{ display: 'inline-block', animation: isRefreshing ? 'spin 1s infinite linear' : 'none' }}>🔄</span>
                </button>
                <button style={styles.closeX} onClick={() => setActiveModal(null)}>EXIT</button>
              </div>
            </div>
            
            <div style={{...styles.chatBody, backgroundColor: isDarkMode ? "#020617" : "#f8fafc"}}>
              {messages.map((m, i) => (
                <div key={m._id || i} style={m.senderRole === "user" ? styles.rowRight : styles.rowLeft}>
                  <div style={m.senderRole === "user" ? {...styles.bubbleUser, backgroundColor: colors.brand, opacity: m.isOptimistic ? 0.6 : 1} : {...styles.bubbleAdmin, backgroundColor: isDarkMode ? "#1e293b" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}}>
                    {m.text && <div style={{ marginBottom: m.file ? '8px' : '0' }}>{m.text}</div>}
                    {m.file && (
                      <div style={styles.imageWrapper}>
                        <img src={getFullImageUrl(m.file)} alt="Transmit" style={styles.chatImage} />
                      </div>
                    )}
                    <div style={{ fontSize: 9, opacity: 0.7, marginTop: 4, textAlign: 'right' }}>
                      {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {m.senderRole === "user" && !m.isOptimistic && " ✓"}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div style={{...styles.chatInputContainer, backgroundColor: colors.card, borderTop: `1px solid ${colors.border}`}}>
              {previewFileName && (
                <div style={{ fontSize: '10px', color: colors.brand, marginBottom: '5px', paddingLeft: '48px' }}>
                  📎 Attached: {previewFileName}
                </div>
              )}
              <div style={styles.chatInputRow}>
                <label style={styles.attachBtn}>
                  📎
                  <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={(e) => setPreviewFileName(e.target.files[0]?.name)} />
                </label>
                <input 
                  style={{...styles.chatInput, backgroundColor: isDarkMode ? "#0f172a" : "#fff", color: colors.text, border: `1px solid ${colors.border}`}} 
                  value={inputText} onChange={e => setInputText(e.target.value)} 
                  placeholder="Type here..." 
                  onKeyPress={e => e.key === 'Enter' && sendSupportMessage()} 
                  disabled={formLoading}
                />
                <button style={{...styles.sendBtn, backgroundColor: colors.brand}} onClick={sendSupportMessage} disabled={formLoading}>
                  {formLoading ? "..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <style>{` @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } `}</style>
    </div>
  );
}

const styles = {
  layout: { minHeight: "100vh", fontFamily: "'Inter', sans-serif", transition: "0.3s background-color ease" },
  container: { width: "95%", maxWidth: "600px", margin: "180px auto 20px auto" },
  card: { padding: "25px", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.4)", marginBottom: "20px" },
  profileHeader: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' },
  avatarWrapper: { position: 'relative', width: '100px', height: '100px', marginBottom: '15px' },
  avatarImg: { width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' },
  uploadIcon: { position: 'absolute', bottom: '2px', right: '2px', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.3)', color: '#fff' },
  tierRow: { display: "flex", gap: '10px', alignItems: "center" },
  tierBadge: { padding: "4px 10px", borderRadius: "8px", fontSize: "10px", fontWeight: "900" },
  levelBadge: { color: "#94A3B8", fontSize: "11px", fontWeight: "800" },
  progressBox: { marginBottom: "15px" },
  progressLabels: { display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", fontWeight: "700", color: "#94A3B8" },
  progressBar: { height: "6px", borderRadius: "10px", overflow: "hidden" },
  progressFill: { height: "100%" },
  userEmail: { fontSize: "11px", color: "#94A3B8", textAlign: "center", fontFamily: "monospace" },
  menuGroup: { borderRadius: "24px", overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.3)" },
  menuItem: { width: "100%", padding: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "transparent", border: "none", cursor: "pointer", transition: "0.2s" },
  menuText: { fontSize: "14px", fontWeight: "700" },
  menuTextMuted: { fontSize: "12px" },
  menuArrow: { fontSize: "18px", color: "#94A3B8" },
  badge: { marginLeft: "10px", backgroundColor: "#EF4444", color: "#fff", fontSize: "9px", padding: "2px 6px", borderRadius: "4px", fontWeight: "900" },
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(2, 6, 23, 0.9)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 2000, backdropFilter: "blur(8px)" },
  modal: { width: "90%", maxWidth: "400px", padding: "30px", borderRadius: "24px", textAlign: "center" },
  modalTitle: { marginBottom: "20px", fontSize: "16px", fontWeight: "800" },
  verticalStack: { display: "flex", flexDirection: "column", gap: "10px" },
  stackBtn: { padding: "14px", borderRadius: "12px", fontWeight: "700", fontSize: "13px", cursor: "pointer" },
  actionBtn: { padding: "14px", borderRadius: "12px", border: "none", color: "#fff", fontWeight: "800", cursor: "pointer" },
  input: { padding: "14px", borderRadius: "12px", outline: "none", fontSize: "14px" },
  closeLink: { background: "none", border: "none", color: "#94A3B8", marginTop: "15px", fontSize: "12px", cursor: "pointer", fontWeight: "600" },
  chatWindow: { width: "98%", maxWidth: "450px", height: "70vh", borderRadius: "24px", display: "flex", flexDirection: "column", overflow: "hidden" },
  chatHeader: { padding: "15px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#fff" },
  chatTitle: { fontSize: "13px", fontWeight: "800" },
  chatStatus: { fontSize: "8px", fontWeight: "900" },
  refreshBtn: { background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: "14px" },
  closeX: { border: "none", background: "rgba(255,255,255,0.1)", color: "#fff", fontSize: "9px", padding: "4px 8px", borderRadius: "4px", cursor: "pointer" },
  chatBody: { flex: 1, padding: "15px", overflowY: "auto" },
  rowRight: { display: "flex", justifyContent: "flex-end", marginBottom: "12px" },
  rowLeft: { display: "flex", justifyContent: "flex-start", marginBottom: "12px" },
  bubbleUser: { color: "#fff", padding: "10px 12px", borderRadius: "12px 12px 0 12px", fontSize: "13px", maxWidth: "85%" },
  bubbleAdmin: { padding: "10px 12px", borderRadius: "12px 12px 12px 0", fontSize: "13px", maxWidth: "85%" },
  imageWrapper: { marginTop: "5px", borderRadius: "8px", overflow: "hidden" },
  chatImage: { display: "block", width: "100%", maxHeight: "250px", objectFit: "cover" },
  chatInputContainer: { padding: "15px" },
  chatInputRow: { display: "flex", gap: "8px" },
  attachBtn: { width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.05)", color: "#fff", borderRadius: "10px", cursor: "pointer" },
  chatInput: { flex: 1, padding: "8px 15px", borderRadius: "10px", outline: "none" },
  sendBtn: { color: "#fff", border: "none", padding: "0 15px", borderRadius: "10px", fontWeight: "800", cursor: "pointer" }
};